<template>
  <!--파트너코드-->
  <div class="container-formlogin mb-4">
    <p v-if="loginTitle" class="accent">{{ loginTitle }}</p>
    <input
      v-else
      v-model="loginForm.partnerCode"
      :placeholder="placeholder"
      :class="{ 'input-error': isErrorPartnerCode }"
      autocapitalize="none"
      @keyup.enter.prevent="onPressEnter"
    />
    <!--아이디-->
    <input
      v-model="loginForm.userId"
      :placeholder="t('input_login_id')"
      :class="{ 'input-error': isErrorIdPw }"
      autocapitalize="none"
      autocomplete="username"
      @input="blockInputInvalidId"
      @keyup.enter.prevent="onPressEnter"
    />
    <!--비밀번호-->
    <div class="wrapper-pw">
      <input
        v-model="loginForm.password"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="t('input_login_pw')"
        :class="{ pw: true, 'input-error': isErrorIdPw }"
        autocomplete="current-password"
        @keydown="blockInputInvalidPassword"
        @keyup.enter.prevent="onPressEnter"
      />
      <IconEye
        class="icon-medium eye-icon"
        :class="{ 'eye-show': showPassword, 'eye-hide': !showPassword }"
        @click="showPassword = !showPassword"
      />
    </div>
  </div>
  <!--에러메시지-->
  <div v-if="errorLogin" class="error-message">
    <IconInfo class="icon-smaller icon-error" />
    <p class="small error">{{ t(errorLogin.messageDisplayed) }}</p>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { fetchInfoPartner } from '@/api/apiService';
import IconEye from '@/components/icons/IconEye.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import { TypeDomain } from '@/types/common';
import { ILoginForm } from '@/types/components';
import { IErrorLogin } from '@/types/components';
import { blockInputInvalidId, blockInputInvalidPassword } from '@/utils/validatorInput';

const { t } = useI18n();
const route = useRoute();

const props = withDefaults(
  defineProps<{
    typeLogin?: string;
    domain?: TypeDomain;
  }>(),
  {
    typeLogin: '',
    domain: 'CHAT',
  }
);

const loginForm = defineModel<ILoginForm>('loginForm', {
  required: true,
  default: () => ({
    userId: '',
    password: '',
    partnerCode: '',
    loginType: '',
  }),
});

const errorLogin = defineModel('errorLogin') as Ref<IErrorLogin>;

const emit = defineEmits<{
  handleLogin: [];
}>();

const showPassword = ref(false); // 비밀번호 보기
const loginTitle = ref<string>(''); // 로그인 폼 제목

const isErrorPartnerCode = computed(() => errorLogin.value?.errorCode === 1015); // 로그인에러 원인이 파트너 코드인지 여부
const isErrorIdPw = computed(() => errorLogin.value?.errorCode === 1004); // 로그인에러 원인이 아이디/비번인지 여부

const placeholder = computed<string>(() => {
  return (
    {
      CHAT: t('login_select_partner_code'),
      SELF_SERVICE: t('login_select_organization_id'),
    }[props.domain] || t('login_select_partner_code')
  );
});

const onPressEnter = (e: KeyboardEvent) => {
  emit('handleLogin');
  (e.target as HTMLInputElement).blur();
};

// 파트너 정보 조회
const getPartnerInfo = async () => {
  let partnerCode = String(route.query.partnerCode ?? '').trim();
  if (partnerCode === 'undefined' || partnerCode === 'null') {
    partnerCode = '';
  }

  if (!partnerCode) {
    const isTypeDist = props.typeLogin === 'DISTRIBUTOR';
    let partnerCodeSaved = localStorage.getItem(isTypeDist ? 'partner-code-dist' : 'partner-code-admin');

    if (partnerCodeSaved === 'undefined' || partnerCodeSaved === 'null') {
      partnerCodeSaved = '';
      localStorage.removeItem(props.typeLogin === 'DISTRIBUTOR' ? 'partner-code-dist' : 'partner-code-admin');
    }

    if (partnerCodeSaved) loginForm.value.partnerCode = partnerCodeSaved;
    return;
  }
  const { success, data: infoPartner } = await fetchInfoPartner(partnerCode);
  if (!success) return;
  loginTitle.value = props.domain === 'SELF_SERVICE' ? infoPartner.partnerCode : infoPartner.partnerName;
  loginForm.value.partnerCode = infoPartner.partnerCode;
  loginForm.value.loginType = infoPartner.partnerType;
};

getPartnerInfo();
</script>

<style scoped lang="scss">
.container-formlogin {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.input-error {
  border-color: var(--error);
  box-shadow: none;
}

.id {
  margin-bottom: 0.5rem;
}

.wrapper-pw {
  position: relative;
  width: 100%;
}

.pw {
  width: 100%;
  padding-right: 3rem;
}

.eye-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.eye-show {
  fill: var(--font-default);
}

.eye-hide {
  fill: var(--font-dim);
}

.error-message {
  width: 100%;
  display: flex;
  justify-content: left;
  margin-top: 0.25rem;
  gap: 0.25rem;
  text-align: left;
}

.icon-error {
  margin-top: 0.15rem;
}
</style>
